// Customer login — passwordless email-OTP by default, with a fallback
// "Sign in with password" mode for admin/staff. On success we redirect to
// ?next=, /my-listings for customers, /staff for staff, /admin otherwise.
import React, { useEffect, useState } from 'react';
import { OtpInput } from '../components/OtpInput';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, AtSign, Lock, ArrowRight, KeyRound, MailCheck, Eye, EyeOff } from 'lucide-react';

const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(' ');
  return typeof d === 'string' ? d : (e?.message || 'Something went wrong');
};

const Login = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login, loginOtpStart, loginOtpVerify } = useAuth();

  // 'otp' = email-code flow; 'password' = classic password (admin/staff).
  const [mode, setMode] = useState('otp');
  const [step, setStep] = useState(1);   // 1 = enter email, 2 = enter code
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const routeAfterLogin = (me) => {
    const next = sp.get('next');
    if (next) nav(next, { replace: true });
    else if (me?.role === 'customer') nav('/my-listings', { replace: true });
    else if (me?.role === 'staff') nav('/staff', { replace: true });
    else nav('/admin', { replace: true });
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    setErr(''); setInfo('');
    setBusy(true);
    try {
      const data = await loginOtpStart({ email });
      setInfo(data?.message || 'Check your inbox for the 6-digit code.');
      setStep(2);
      setCooldown(30);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e, typed) => {
    e?.preventDefault?.();
    const code = typed || otp;
    if (busy) return;
    if (code.length < 6) { setErr('Enter all 6 digits of the code from your email.'); return; }
    setErr('');
    setBusy(true);
    try {
      const me = await loginOtpVerify({ email, otp: code });
      routeAfterLogin(me);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const me = await login(email, password);
      routeAfterLogin(me);
    } catch (ex) {
      setErr(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setStep(1);
    setOtp('');
    setPassword('');
    setErr('');
    setInfo('');
  };

  return (
    <div className="min-h-screen pt-28 sm:pt-32 pb-16 bg-[#0b1722] text-white">
      <div className="absolute inset-x-0 top-0 h-[60vh] -z-0 bg-[radial-gradient(60%_50%_at_80%_15%,rgba(20,184,166,0.20),transparent_60%)]" />
      <div className="relative max-w-md mx-auto px-4 sm:px-6">
        <Link to="/" className="text-xs text-teal-300 inline-flex items-center gap-1 mb-6">
          <ShieldCheck size={12} /> UNISOL GLOBAL
        </Link>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
          {mode === 'otp' && step === 2 ? 'Enter the code' : 'Welcome back'}
        </h1>
        <p className="text-slate-300 mt-2 text-sm">
          {mode === 'otp' && step === 2
            ? <>If <b className="text-white break-all">{email}</b> has a customer account, we emailed it a 6-digit code.</>
            : mode === 'otp'
            ? 'Sign in with a one-time code sent to your email.'
            : 'Sign in with your admin/staff password.'}
        </p>

        {/* Mode toggle */}
        <div className="mt-5 grid grid-cols-2 gap-1 bg-white/5 border border-white/10 rounded-full p-1" data-testid="login-mode-toggle">
          <button
            type="button"
            onClick={() => switchMode('otp')}
            className={`text-xs font-semibold py-2 rounded-full transition ${
              mode === 'otp' ? 'bg-teal-400 text-slate-900' : 'text-slate-300 hover:text-white'
            }`}
            data-testid="login-mode-otp"
          >
            <MailCheck size={12} className="inline mr-1" /> Email code
          </button>
          <button
            type="button"
            onClick={() => switchMode('password')}
            className={`text-xs font-semibold py-2 rounded-full transition ${
              mode === 'password' ? 'bg-teal-400 text-slate-900' : 'text-slate-300 hover:text-white'
            }`}
            data-testid="login-mode-password"
          >
            <KeyRound size={12} className="inline mr-1" /> Password
          </button>
        </div>

        {mode === 'otp' ? (
          <form
            onSubmit={step === 1 ? sendCode : verifyCode}
            className="mt-6 bg-white/[0.04] border border-white/10 rounded-2xl p-5 sm:p-6 space-y-4"
            data-testid="login-form-otp"
          >
            {step === 1 ? (
              <label className="block">
                <span className="block text-xs font-medium text-slate-300 mb-1">Email</span>
                <div className="relative">
                  <AtSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-slate-900/60 border border-white/15 text-white placeholder-slate-500 rounded-lg pl-9 pr-3 py-3 text-base sm:text-sm outline-none focus:ring-2 focus:ring-teal-400"
                    placeholder="you@example.com"
                    data-testid="login-otp-email"
                  />
                </div>
              </label>
            ) : (
              <OtpInput value={otp} onChange={setOtp} testid="login-otp-input" boxTestid="login-otp" onComplete={(c) => verifyCode(null, c)} />
            )}

            {err && (
              <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs rounded-lg p-3" data-testid="login-error">{err}</div>
            )}
            {info && !err && step === 2 && (
              <div className="bg-teal-500/10 border border-teal-500/30 text-teal-200 text-xs rounded-lg p-3" data-testid="login-info">
                {info}
                <span className="block mt-1.5 text-teal-200/80" data-testid="login-info-password-hint">
                  Workshop owners, admins and staff don&apos;t get a code — please use{' '}
                  <button type="button" onClick={() => switchMode('password')} className="underline font-semibold" data-testid="login-info-use-password">Password</button> instead.
                </span>
              </div>
            )}

            <button
              type="submit" disabled={busy}
              className="w-full bg-teal-400 hover:bg-teal-300 disabled:opacity-60 text-slate-900 font-semibold py-3 rounded-lg text-sm inline-flex items-center justify-center gap-2"
              data-testid={step === 1 ? 'login-send-otp' : 'login-verify-otp'}
            >
              {busy
                ? (step === 1 ? 'Sending code…' : 'Verifying…')
                : (step === 1 ? <>Send sign-in code <ArrowRight size={14} /></> : <>Sign in <ArrowRight size={14} /></>)}
            </button>

            {step === 2 && (
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button" onClick={() => { setStep(1); setOtp(''); setErr(''); }}
                  className="text-slate-400 hover:text-white"
                  data-testid="login-otp-change-email"
                >
                  ← Change email
                </button>
                <button
                  type="button" disabled={cooldown > 0 || busy}
                  onClick={() => { setOtp(''); sendCode(); }}
                  className="text-teal-300 hover:text-teal-200 disabled:text-slate-500"
                  data-testid="login-otp-resend"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            )}
          </form>
        ) : (
          <form onSubmit={submitPassword} className="mt-6 bg-white/[0.04] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="login-form-password">
            <label className="block">
              <span className="block text-xs font-medium text-slate-300 mb-1">Email</span>
              <div className="relative">
                <AtSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-slate-900/60 border border-white/15 text-white placeholder-slate-500 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400"
                  data-testid="login-email" />
              </div>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-slate-300 mb-1">Password</span>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input type={showPassword ? 'text' : 'password'} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-slate-900/60 border border-white/15 text-white placeholder-slate-500 rounded-lg pl-9 pr-11 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400"
                  autoComplete="current-password"
                  data-testid="login-password" />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            {err && (
              <div className="bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs rounded-lg p-3" data-testid="login-error">{err}</div>
            )}

            <button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full bg-teal-400 hover:bg-teal-300 disabled:opacity-60 text-slate-900 font-semibold py-3 rounded-lg text-sm inline-flex items-center justify-center gap-2">
              {busy ? 'Signing in…' : <>Sign in <ArrowRight size={14} /></>}
            </button>
          </form>
        )}

        <p className="text-xs text-slate-400 text-center pt-4">
          New to Unisol?{' '}
          <Link to="/signup" className="text-teal-300 hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
